/** Create / edit dialog for a language alias (react-hook-form + zod).
 *
 *  An alias maps an arbitrary value a policy may return (matched
 *  case-insensitively, stored lowercased) onto a target ISO 639-1 language code
 *  picked from the tenant's selectable languages. `tenant` is clearable:
 *  cleared = a system-wide alias (`tour_operator: null`); the scope also drives
 *  which languages surface first. No optimistic-concurrency token, so writes are
 *  last-writer-wins. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createAlias,
  update as updateAlias,
  type Alias,
  type AliasWrite,
} from '@/api/languageAliases';
import { listLanguages } from '@/api/languagePolicies';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

import { ALIAS_HELP } from './fieldHelp';

const FormSchema = z.object({
  alias: z.string().trim().min(1, 'Alias is required').max(256),
  language_code: z.string().trim().min(1, 'Language is required'),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { alias: '', language_code: '', enabled: true };

function toDefaults(alias: Alias | null): FormValues {
  if (!alias) return EMPTY;
  return {
    alias: alias.alias,
    language_code: alias.language_code ?? '',
    enabled: alias.enabled,
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  alias: Alias | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function LanguageAliasFormDialog({ open, alias, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = alias !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.language_alias',
    objectPk: alias?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['language_aliases'] }),
  });

  // Tenant lives outside RHF (self-contained controlled input). null = system-wide.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(alias),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(alias));
    if (alias) {
      setTenant(
        alias.tenant
          ? { id: alias.tenant.id, name: alias.tenant.name, code: alias.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alias?.id]);

  const tenantId = tenant?.id ?? null;

  // Selectable languages — the tenant's enabled languages first.
  const { data: languagesData } = useQuery({
    queryKey: ['languages', tenantId],
    queryFn: () => listLanguages(tenantId),
    staleTime: 60_000,
  });
  const languages = languagesData ?? [];

  const mutation = useMutation({
    mutationFn: (body: AliasWrite) => (isEdit ? updateAlias(alias.id, body) : createAlias(body)),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Language alias updated.' : 'Language alias created.', 'success');
      qc.invalidateQueries({ queryKey: ['language_aliases'] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    mutation.mutate({
      tour_operator: tenantId,
      alias: v.alias.trim(),
      language_code: v.language_code,
      enabled: v.enabled,
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title={
        <>
          {isEdit ? `Edit language alias · ${alias.alias}` : 'New language alias'}{' '}
          <HelpLink story="language-policies" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create alias'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          {...register('alias')}
          label="Alias"
          size="small"
          fullWidth
          error={Boolean(errors.alias)}
          helperText={
            errors.alias?.message ?? 'The value a policy may return (matched case-insensitively).'
          }
          InputProps={{ endAdornment: <FieldHelp help={ALIAS_HELP.alias} /> }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Controller
            control={control}
            name="language_code"
            render={({ field }) => {
              // Keep the current value selectable even before the list resolves.
              const options =
                field.value && !languages.some((l) => l.code === field.value)
                  ? [{ code: field.value, name: field.value, enabled: false }, ...languages]
                  : languages;
              return (
                <TextField
                  select
                  label="Language"
                  size="small"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={Boolean(errors.language_code)}
                  helperText={errors.language_code?.message ?? 'Target ISO 639-1 language code.'}
                >
                  {options.map((l) => (
                    <MenuItem key={l.code} value={l.code}>
                      {l.name} ({l.code})
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
          <FieldHelp help={ALIAS_HELP.language} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <TenantPicker
              value={tenant}
              featured={isEdit ? undefined : pageTenants}
              onChange={setTenant}
              label="Operator"
              placeholder="System / global (no operator)"
            />
          </Box>
          <FieldHelp help={ALIAS_HELP.operator} />
        </Box>

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={labelWithHelp('Enabled', ALIAS_HELP.enabled)}
            />
          )}
        />
      </Box>
      )}
    </AppDialog>
  );
}
